<template>
  <section ref="laneRef" class="tw:relative tw:rounded-xl tw:bg-zinc-100 tw:px-3 tw:pt-2.5 tw:pb-3">
    <header class="tw:flex tw:justify-between tw:px-0.5 tw:pb-2 tw:text-xs tw:text-zinc-500">
      <span>{{ lane.label }}</span>
      <span>{{ $t('{n} message | {n} messages', lane.branches.length) }}</span>
    </header>

    <!-- Side by side from md up, linked by drawn paths; stacked below, each branch hanging from a rail. -->
    <div class="tw:grid tw:grid-cols-1 tw:items-start tw:gap-y-2.5 tw:md:grid-cols-3 tw:md:gap-x-7">
      <div ref="triggerRef"
           class="tw:rounded-2xl tw:border tw:border-violet-400 tw:bg-white tw:md:col-start-1 tw:md:[grid-row:1/span_var(--span)]"
           :style="{ '--span': lane.branches.length }">
        <div data-anchor class="tw:flex tw:items-center tw:gap-2 tw:px-2.5 tw:py-2 tw:text-sm tw:font-medium tw:text-zinc-900">
          <span class="tw:flex tw:size-5.5 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-violet-500 tw:text-white"
                aria-hidden="true">
            <Zap class="tw:size-3.5" />
          </span>
          <span class="tw:truncate">{{ lane.name }}</span>
        </div>
        <dl v-if="lane.category || lane.help"
            class="tw:m-0 tw:border-t tw:border-zinc-200 tw:px-2.5 tw:py-1.5 tw:text-xs tw:leading-6 tw:text-zinc-500">
          <div v-if="lane.category" class="tw:flex tw:justify-between tw:gap-3">
            <dt class="tw:font-normal">{{ $t('Type') }}</dt>
            <dd class="tw:m-0 tw:text-right tw:text-zinc-900">{{ lane.category }}</dd>
          </div>
          <div v-if="lane.help" class="tw:leading-5">{{ lane.help }}</div>
        </dl>
      </div>

      <template v-for="(branch, index) in lane.branches" :key="branch.message.id">
        <div :ref="(el) => setConditionRef(el, index)" data-role="condition"
             :class="[RAIL, 'tw:max-md:ml-5 tw:max-md:before:-left-3 tw:md:col-start-2 tw:md:[grid-row:var(--row)]', { 'tw:max-md:mt-2': index > 0 }]"
             :style="{ '--row': index + 1 }">
          <ConditionCard :conditions="branch.conditions" />
        </div>
        <div :ref="(el) => setMessageRef(el, index)" data-role="message"
             :class="[RAIL, 'tw:max-md:ml-9 tw:max-md:before:-left-7 tw:md:col-start-3 tw:md:[grid-row:var(--row)]']"
             :style="{ '--row': index + 1 }">
          <MessageCard :message="branch.message" />
        </div>
      </template>

      <a v-if="addUrl" :href="addUrl"
         class="tw:flex tw:items-center tw:justify-center tw:gap-1.5 tw:rounded-lg tw:border tw:border-dashed tw:border-zinc-400 tw:py-2 tw:text-xs tw:text-zinc-500 tw:no-underline tw:hover:border-zinc-600 tw:hover:bg-white tw:hover:text-zinc-900 tw:hover:no-underline tw:max-md:mt-2 tw:max-md:ml-5 tw:md:col-[2/4] tw:md:[grid-row:var(--row)]"
         :style="{ '--row': lane.branches.length + 1 }">
        <Plus class="tw:size-3.5" aria-hidden="true" /> {{ $t('Add a branch') }}
      </a>
    </div>

    <svg class="tw:pointer-events-none tw:absolute tw:inset-0 tw:size-full tw:overflow-visible" aria-hidden="true">
      <path v-for="(path, index) in paths" :key="index" :d="path" fill="none" class="tw:stroke-zinc-400" stroke-width="1" />
    </svg>
  </section>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch, type ComponentPublicInstance } from 'vue'
import { Plus, Zap } from '@lucide/vue'
import ConditionCard from './ConditionCard.vue'
import MessageCard from './MessageCard.vue'
import type { BoardLane } from './types'

const props = withDefaults(defineProps<{
  lane: BoardLane;
  /** Where to add a message on this trigger, none when the team cannot. */
  addUrl?: string | null;
}>(), {
  addUrl: null,
})

/** Below md, a branch hangs from a vertical rail on its left instead of drawn paths. */
const RAIL = "tw:max-md:relative tw:max-md:before:absolute tw:max-md:before:-top-2.5 tw:max-md:before:h-[calc(100%+0.625rem)] tw:max-md:before:w-px tw:max-md:before:bg-zinc-300 tw:max-md:before:content-['']"

const SIDE_BY_SIDE = '(min-width: 768px)'

const laneRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const conditionRefs: HTMLElement[] = []
const messageRefs: HTMLElement[] = []
const paths = ref<string[]>([])

const setConditionRef = (el: Element | ComponentPublicInstance | null, index: number): void => {
  if (el instanceof HTMLElement) conditionRefs[index] = el
}
const setMessageRef = (el: Element | ComponentPublicInstance | null, index: number): void => {
  if (el instanceof HTMLElement) messageRefs[index] = el
}

/** Where a link meets a block: the middle of its title row, on its left or right edge. */
const anchor = (element: HTMLElement, origin: DOMRect): { left: number; right: number; y: number } => {
  const box = element.getBoundingClientRect()
  const head = (element.matches('[data-anchor]') ? element : element.querySelector('[data-anchor]')) ?? element
  const headBox = head.getBoundingClientRect()

  return {
    left: box.left - origin.left,
    right: box.right - origin.left,
    y: headBox.top - origin.top + headBox.height / 2,
  }
}

/** Right-angled links: trigger to each condition, each condition to its message. */
const draw = (): void => {
  if (!laneRef.value || !triggerRef.value) return
  if (!window.matchMedia(SIDE_BY_SIDE).matches) {
    paths.value = []

    return
  }
  const origin = laneRef.value.getBoundingClientRect()
  const trigger = anchor(triggerRef.value, origin)

  paths.value = props.lane.branches.flatMap((_, index) => {
    const condition = conditionRefs[index]
    const message = messageRefs[index]
    if (!condition || !message) return []
    const from = anchor(condition, origin)
    const to = anchor(message, origin)
    const toCondition = (trigger.right + from.left) / 2
    const toMessage = (from.right + to.left) / 2

    return [
      `M${trigger.right} ${trigger.y} H${toCondition} V${from.y} H${from.left}`,
      `M${from.right} ${from.y} H${toMessage} V${to.y} H${to.left}`,
    ]
  })
}

let observer: ResizeObserver | null = null

onMounted(() => {
  observer = new ResizeObserver(draw)
  if (laneRef.value) observer.observe(laneRef.value)
  nextTick(draw)
})

onBeforeUnmount(() => observer?.disconnect())

watch(() => props.lane, () => nextTick(draw), { deep: true })
</script>
